import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatMenuModule } from '@angular/material/menu';
import { MatButtonModule } from '@angular/material/button';
import { MatToolbarModule } from '@angular/material/toolbar';
import { ActivatedRoute, NavigationEnd, Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { MsalModule } from '@azure/msal-angular';
import { Subject, filter } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
// layouts/standard-layout/standard-layout.component.ts
import { AuthService } from '../../Shared/Services/authentication.service';
import { UserResponse } from 'src/app/Shared/Models/User.model';
import { PrimeNgModule } from 'src/app/Shared/prime-ng/prime-ng.module';
import { MenuService } from 'src/app/Services/menu.service';
import { MenuItem } from 'src/app/Shared/Models/Menu.model';

interface SidebarMenuGroup {
  key: string;
  label: string;
  icon: string;
  items: MenuItem[];
}

@Component({
  selector: 'app-standard-layout',
  standalone: true,
  imports: [PrimeNgModule, CommonModule, MsalModule, RouterOutlet, RouterLink, RouterLinkActive, MatToolbarModule, MatButtonModule, MatMenuModule],
  templateUrl: './standard-layout.component.html',
  styleUrl: './standard-layout.component.css'
})
export class StandardLayoutComponent implements OnInit, OnDestroy {
  title = 'LabTech';
  isIframe = false;
  isToggle = false;
  isMobileMenuOpen = false;
  isFullScreenLayout = false;
  openMenuId: string | null = null;

  currentUser: UserResponse | null = null;
  userDisplayName = '';
  userProfileImage: string | null = null;
  menuItems: MenuItem[] = [];
  topLevelMenus: MenuItem[] = [];
  groupedMenus: SidebarMenuGroup[] = [];

  private readonly destroy$ = new Subject<void>();

  constructor(
    private auth: AuthService,
    private menuService: MenuService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
  ) { }

  ngOnInit(): void {
    this.isIframe = window !== window.parent && !window.opener;

    this.auth.currentUser$
      .pipe(takeUntil(this.destroy$))
      .subscribe(user => {
        this.currentUser = user;
        this.userDisplayName = this.auth.getUserDisplayName();
        this.userProfileImage = this.auth.getUserProfileImage();

        if (user) {
          this.menuService.loadCurrentMenus()
            .pipe(takeUntil(this.destroy$))
            .subscribe();

          this.auth.loadMsalProfileImage()
            .pipe(takeUntil(this.destroy$))
            .subscribe(image => {
              this.userProfileImage = image;
            });
        }
      });

    this.menuService.currentMenus$
      .pipe(takeUntil(this.destroy$))
      .subscribe(menus => this.setMenus(menus));

    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this.destroy$),
      )
      .subscribe(() => this.updateFullScreenLayoutState());

    this.updateFullScreenLayoutState();
  }

  logout(): void {
    this.auth.logout(true); // true = popup, false = redirect
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  toggleSidebar(): void {
    this.isToggle = !this.isToggle;
  }

  toggleMenu(menuId: string): void {
    this.openMenuId = this.openMenuId === menuId ? null : menuId;
  }

  isMenuOpen(menuId: string): boolean {
    return this.openMenuId === menuId;
  }

  toggleMobileMenu(): void {
    this.isMobileMenuOpen = !this.isMobileMenuOpen;
    document.body.style.overflow = this.isMobileMenuOpen ? 'hidden' : '';
  }

  closeMobileMenu(): void {
    this.isMobileMenuOpen = false;
    document.body.style.overflow = '';
  }

  private updateFullScreenLayoutState(): void {
    let currentRoute = this.activatedRoute;

    while (currentRoute.firstChild) {
      currentRoute = currentRoute.firstChild;
    }

    const isPlanView = currentRoute.snapshot.routeConfig?.path === 'plan-view';
    const isFullScreen = currentRoute.snapshot.queryParamMap.get('fullScreen') === '1';

    this.isFullScreenLayout = isPlanView && isFullScreen;

    if (this.isFullScreenLayout) {
      this.closeMobileMenu();
    }
  }

  get isAuditor(): boolean {
    return this.currentUser?.role?.toLowerCase() === 'auditor';
  }

  public trackByMenuKey(_: number, item: MenuItem): string {
    return item.key;
  }

  public trackByGroupKey(_: number, group: SidebarMenuGroup): string {
    return group.key;
  }

  private setMenus(menus: MenuItem[]): void {
    this.menuItems = [...menus].sort((a, b) => a.sortOrder - b.sortOrder);
    this.topLevelMenus = this.menuItems.filter(menu => !menu.groupKey);

    const groups = new Map<string, SidebarMenuGroup>();
    for (const menu of this.menuItems.filter(item => item.groupKey)) {
      const key = menu.groupKey!;
      if (!groups.has(key)) {
        groups.set(key, {
          key,
          label: menu.groupLabel || key,
          icon: menu.groupIcon || 'folder',
          items: [],
        });
      }

      groups.get(key)!.items.push(menu);
    }

    this.groupedMenus = [...groups.values()]
      .map(group => ({
        ...group,
        items: group.items.sort((a, b) => a.sortOrder - b.sortOrder),
      }))
      .sort((a, b) => a.items[0].sortOrder - b.items[0].sortOrder);
  }
}
